import {
  readAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";
import {
  DEFAULT_BRIEF_SEQUENCE_VERSION,
  defaultBriefSequenceForCountry,
  isRetiredBriefSectionId,
  normalizeBriefSectionIds,
} from "./default-sequence";
import { briefProfileForMarketCountryChange } from "./market-country-profile-policy.mjs";
import {
  creditBriefStreakCompletion,
  maximumAcceptedBriefStreakDate,
  mergeBriefStreakStates,
  normalizeBriefStreakState,
  type BriefStreakSyncState,
} from "./brief-streak-sync.mjs";

const BRIEF_PROFILE_KEY = "tb_public_web_brief_profile_v1";
const BRIEF_PROFILES_KEY = "tb_public_web_brief_profiles_v1";
const BRIEF_HISTORY_KEY = "tb_public_web_brief_history_v1";
const BRIEF_STREAK_KEY = "tb_public_web_brief_streak_v1";
export const BRIEF_PROFILE_CHANGED_EVENT = "tb-public-web-brief-profile-changed";
export const BRIEF_STREAK_CHANGED_EVENT = "tb-public-web-brief-streak-changed";
export const MAX_BRIEF_PROFILES = 8;
export const MAX_CUSTOM_BRIEF_PROFILES = 7;
export const MAX_HIDDEN_READY_MADE_BRIEF_PROFILES = 1;

/**
 * `BriefDepth` is retained for the deployed web onboarding payload. The
 * canonical Android wire value is `BriefProfileLength`; helpers below keep the
 * two representations in lockstep while old `focused` profiles migrate to
 * Android's Quick edition without losing their selected section order.
 */
export type BriefDepth = "focused" | "balanced" | "deep";
export type BriefProfileLength = "quick" | "balanced" | "deep";
export type BriefReadMinutes = 3 | 5 | 8;
export type BriefAudioMinutes = 2 | 5;
export type BriefStyleId =
  | "daily"
  | "quick_5"
  | "morning"
  | "investor"
  | "sports_fan"
  | "learning"
  | "news_power";
export type BriefTone = "calm" | "executive" | "coaching" | "analytical";
export type BriefProfilePurpose =
  | "daily_clarity"
  | "executive"
  | "deep_focus"
  | "life_admin"
  | "learning"
  | "markets"
  | "sports"
  | "custom";
export type BriefProfileTimeOfDay = "morning" | "midday" | "evening" | "anytime";

export type BriefProfile = {
  id: string;
  name: string;
  depth: BriefDepth;
  preferredLength: BriefProfileLength;
  /**
   * Compatibility duration for the existing web audio endpoint, which still
   * accepts only 2/5 minute generation requests. Product read-time copy must
   * use `briefReadMinutesForLength(preferredLength)` instead.
   */
  targetMinutes: BriefAudioMinutes;
  includeMarkets: boolean;
  includeTasks: boolean;
  marketCountry: string;
  sectionIds: string[];
  disabledSectionIds: string[];
  sequenceVersion: string;
  styleId: BriefStyleId;
  templateId: string;
  purpose: BriefProfilePurpose;
  tone: BriefTone;
  timeOfDay: BriefProfileTimeOfDay;
  narrationEnabled: boolean;
  languageCode: string;
  createdAtEpochMs?: number;
  updatedAtEpochMs?: number;
};

export type BriefProfileCollection = {
  profiles: BriefProfile[];
  activeProfileId: string;
};

export type BriefRunRecord = {
  id: string;
  completedAtEpochMs: number;
  sectionCount: number;
  targetMinutes: BriefReadMinutes;
  depth: BriefDepth;
};

const DEFAULT_PROFILE: BriefProfile = {
  id: "web-default",
  name: "Daily Brief",
  depth: "balanced",
  preferredLength: "balanced",
  targetMinutes: 5,
  includeMarkets: true,
  includeTasks: true,
  marketCountry: "US",
  sectionIds: defaultBriefSequenceForCountry("US"),
  disabledSectionIds: [],
  sequenceVersion: DEFAULT_BRIEF_SEQUENCE_VERSION,
  styleId: "daily",
  templateId: "",
  purpose: "daily_clarity",
  tone: "calm",
  timeOfDay: "morning",
  narrationEnabled: false,
  languageCode: "",
};

function storage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

function normalizeDepth(value: unknown): BriefDepth {
  if (value === "focused" || value === "quick") return "focused";
  if (value === "deep") return "deep";
  return "balanced";
}

export function briefProfileLengthForDepth(depth: BriefDepth): BriefProfileLength {
  if (depth === "focused") return "quick";
  return depth;
}

export function briefDepthForProfileLength(length: BriefProfileLength): BriefDepth {
  if (length === "quick") return "focused";
  return length;
}

export function briefReadMinutesForLength(length: BriefProfileLength): BriefReadMinutes {
  if (length === "quick") return 3;
  if (length === "deep") return 8;
  return 5;
}

export function briefAudioMinutesForLength(length: BriefProfileLength): BriefAudioMinutes {
  return length === "quick" ? 2 : 5;
}

export function combinedBriefAudioMinutesForLength(
  length: BriefProfileLength,
  hasPremiumAccess: boolean,
): BriefAudioMinutes {
  return hasPremiumAccess ? briefAudioMinutesForLength(length) : 2;
}

function normalizePreferredLength(value: unknown, depthValue: unknown): BriefProfileLength {
  if (depthValue === "focused" || depthValue === "quick") return "quick";
  if (depthValue === "balanced" || depthValue === "deep") return depthValue;
  if (value === "quick" || value === "deep") return value;
  return "balanced";
}

function normalizeStyleId(value: unknown): BriefStyleId {
  switch (value) {
    case "focus":
    case "quick_5":
      return "quick_5";
    case "market":
    case "investor":
      return "investor";
    case "morning":
    case "starter":
      return "morning";
    case "sports_fan":
      return "sports_fan";
    case "learning":
      return "learning";
    case "deep":
    case "news_power":
      return "news_power";
    case "daily":
    default:
      return "daily";
  }
}

function normalizeTone(value: unknown): BriefTone {
  if (value === "direct" || value === "executive") return "executive";
  if (value === "coach" || value === "coaching") return "coaching";
  if (value === "analytical") return "analytical";
  return "calm";
}

function normalizePurpose(value: unknown): BriefProfilePurpose {
  switch (value) {
    case "executive":
    case "deep_focus":
    case "life_admin":
    case "learning":
    case "markets":
    case "sports":
    case "custom":
      return value;
    default:
      return "daily_clarity";
  }
}

function normalizeTimeOfDay(value: unknown): BriefProfileTimeOfDay {
  if (value === "midday" || value === "evening" || value === "anytime") return value;
  return "morning";
}

function normalizeMarketCountry(value: unknown): string {
  const country = String(value || "US")
    .trim()
    .toUpperCase()
    .slice(0, 2);
  return /^[A-Z]{2}$/.test(country) ? country : "US";
}

function normalizeOptionalSectionIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const sectionIds: string[] = [];
  value.forEach((entry) => {
    const id = String(entry || "")
      .trim()
      .toLowerCase();
    if (!id || id.length > 80 || isRetiredBriefSectionId(id) || seen.has(id)) return;
    seen.add(id);
    sectionIds.push(id);
  });
  return sectionIds;
}

function normalizeEpochMs(value: unknown): number | undefined {
  const epochMs = Number(value);
  return Number.isFinite(epochMs) && epochMs >= 0 ? Math.round(epochMs) : undefined;
}

export function normalizeBriefProfile(
  value: Partial<BriefProfile> | Record<string, unknown> | null | undefined,
  countryHint = "US",
): BriefProfile {
  const raw = (value || {}) as Partial<BriefProfile> & Record<string, unknown>;
  const marketCountry = normalizeMarketCountry(raw.marketCountry || countryHint);
  const preferredLength = normalizePreferredLength(raw.preferredLength, raw.depth);
  const depth = briefDepthForProfileLength(preferredLength);
  return {
    id: String(raw.id || "web-default").trim() || "web-default",
    name: String(raw.name || "Daily Brief").trim() || "Daily Brief",
    depth,
    preferredLength,
    targetMinutes: briefAudioMinutesForLength(preferredLength),
    includeMarkets: raw.includeMarkets !== false,
    includeTasks: raw.includeTasks !== false,
    marketCountry,
    sectionIds: normalizeBriefSectionIds(raw.sectionIds, marketCountry),
    disabledSectionIds: normalizeOptionalSectionIds(raw.disabledSectionIds),
    sequenceVersion: String(raw.sequenceVersion || DEFAULT_BRIEF_SEQUENCE_VERSION),
    styleId: normalizeStyleId(raw.styleId || raw.templateId),
    templateId: String(raw.templateId || "").trim(),
    purpose: normalizePurpose(raw.purpose),
    tone: normalizeTone(raw.tone),
    timeOfDay: normalizeTimeOfDay(raw.timeOfDay),
    narrationEnabled: raw.narrationEnabled === true,
    languageCode: String(raw.languageCode || "").trim(),
    createdAtEpochMs: normalizeEpochMs(raw.createdAtEpochMs),
    updatedAtEpochMs: normalizeEpochMs(raw.updatedAtEpochMs),
  };
}

function defaultProfileForCountry(countryHint: string): BriefProfile {
  const marketCountry = normalizeMarketCountry(countryHint);
  return {
    ...DEFAULT_PROFILE,
    marketCountry,
    sectionIds: defaultBriefSequenceForCountry(marketCountry),
    sequenceVersion: DEFAULT_BRIEF_SEQUENCE_VERSION,
  };
}

export function migrateBriefProfileForCountryChange(
  profile: BriefProfile,
  country: string,
): BriefProfile {
  const marketCountry = normalizeMarketCountry(country);
  return normalizeBriefProfile(
    briefProfileForMarketCountryChange(
      profile,
      marketCountry,
      defaultBriefSequenceForCountry(marketCountry),
      DEFAULT_BRIEF_SEQUENCE_VERSION,
    ),
    marketCountry,
  );
}

export type AndroidBriefProfileWire = {
  id?: unknown;
  name?: unknown;
  sectionIds?: unknown;
  disabledSectionIds?: unknown;
  purpose?: unknown;
  preferredLength?: unknown;
  tone?: unknown;
  timeOfDay?: unknown;
  templateId?: unknown;
  narrationEnabled?: unknown;
  languageCode?: unknown;
  createdAtEpochMs?: unknown;
  updatedAtEpochMs?: unknown;
};

export type AndroidBriefProfileSyncPreferences = {
  briefProfilesJson?: unknown;
  activeBriefProfileId?: unknown;
};

function styleIdForAndroidProfile(profile: AndroidBriefProfileWire): BriefStyleId {
  const templateId = String(profile.templateId || "")
    .trim()
    .toLowerCase();
  if (templateId) return normalizeStyleId(templateId);
  const name = String(profile.name || "")
    .trim()
    .toLowerCase();
  switch (name) {
    case "quick daily brief":
      return "quick_5";
    case "daily starter":
      return "morning";
    case "executive brief":
      return "investor";
    case "sports fan":
      return "sports_fan";
    case "learning":
      return "learning";
    case "deep focus brief":
      return "news_power";
    default:
      return "daily";
  }
}

export function parseAndroidBriefProfiles(value: unknown): AndroidBriefProfileWire[] {
  let parsed = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter(
      (entry): entry is AndroidBriefProfileWire =>
        Boolean(entry) && typeof entry === "object" && !Array.isArray(entry),
    )
    .slice(0, MAX_BRIEF_PROFILES);
}

function briefProfileFromAndroidWire(
  wire: AndroidBriefProfileWire,
  countryHint = "US",
): BriefProfile {
  const marketCountry = normalizeMarketCountry(countryHint);
  const sectionIds = normalizeBriefSectionIds(wire.sectionIds, marketCountry);
  const disabledSectionIds = normalizeOptionalSectionIds(wire.disabledSectionIds).filter((id) =>
    sectionIds.includes(id),
  );
  const preferredLength = normalizePreferredLength(wire.preferredLength, undefined);
  const templateId = String(wire.templateId || "").trim();
  return normalizeBriefProfile(
    {
      id: String(wire.id || "android-active").trim() || "android-active",
      name: String(wire.name || "Daily Brief").trim() || "Daily Brief",
      depth: briefDepthForProfileLength(preferredLength),
      preferredLength,
      targetMinutes: briefAudioMinutesForLength(preferredLength),
      includeMarkets: sectionIds.some((id) => id === "market_pulse" || id === "watchlist"),
      includeTasks: sectionIds.includes("todos"),
      marketCountry,
      sectionIds,
      disabledSectionIds,
      sequenceVersion: DEFAULT_BRIEF_SEQUENCE_VERSION,
      styleId: styleIdForAndroidProfile(wire),
      templateId,
      purpose: normalizePurpose(wire.purpose),
      tone: normalizeTone(wire.tone),
      timeOfDay: normalizeTimeOfDay(wire.timeOfDay),
      narrationEnabled: wire.narrationEnabled === true,
      languageCode: String(wire.languageCode || "").trim(),
      createdAtEpochMs: normalizeEpochMs(wire.createdAtEpochMs),
      updatedAtEpochMs: normalizeEpochMs(wire.updatedAtEpochMs),
    },
    marketCountry,
  );
}

function distinctProfiles(profiles: BriefProfile[]): BriefProfile[] {
  const seen = new Set<string>();
  return profiles.filter((profile) => {
    if (seen.has(profile.id)) return false;
    seen.add(profile.id);
    return true;
  });
}

function retainedBriefProfileIds(
  profiles: BriefProfile[],
  active: BriefProfile | undefined,
  limit: number,
): Set<string> {
  return new Set(
    [...(active ? [active] : []), ...profiles]
      .filter(
        (profile, index, entries) =>
          entries.findIndex((entry) => entry.id === profile.id) === index,
      )
      .slice(0, limit)
      .map((profile) => profile.id),
  );
}

/**
 * Mirrors Android BriefProfileLocalStore.boundedProfiles:
 * - modern collections retain at most seven custom profiles and one hidden
 *   ready-made profile;
 * - a legacy collection containing eight custom profiles keeps all eight and
 *   drops any ready-made row rather than deleting user-authored data.
 */
function boundedBriefProfiles(profiles: BriefProfile[], activeProfileId: string): BriefProfile[] {
  const active = profiles.find((profile) => profile.id === activeProfileId);
  const customProfiles = profiles.filter((profile) => !profile.templateId);
  if (customProfiles.length >= MAX_BRIEF_PROFILES) {
    const retainedCustomIds = retainedBriefProfileIds(
      customProfiles,
      active?.templateId ? undefined : active,
      MAX_BRIEF_PROFILES,
    );
    return profiles.filter((profile) => retainedCustomIds.has(profile.id));
  }
  const retainedIds = new Set([
    ...retainedBriefProfileIds(
      customProfiles,
      active && !active.templateId ? active : undefined,
      MAX_CUSTOM_BRIEF_PROFILES,
    ),
    ...retainedBriefProfileIds(
      profiles.filter((profile) => Boolean(profile.templateId)),
      active?.templateId ? active : undefined,
      MAX_HIDDEN_READY_MADE_BRIEF_PROFILES,
    ),
  ]);
  return profiles.filter((profile) => retainedIds.has(profile.id));
}

export function normalizeBriefProfileCollection(
  value: Partial<BriefProfileCollection> | Record<string, unknown> | null | undefined,
  countryHint = "US",
): BriefProfileCollection {
  const raw = (value || {}) as Partial<BriefProfileCollection>;
  const requestedActiveId = String(raw.activeProfileId || "").trim();
  const profiles = boundedBriefProfiles(
    distinctProfiles(
      (Array.isArray(raw.profiles) ? raw.profiles : []).map((profile) =>
        normalizeBriefProfile(profile, countryHint),
      ),
    ),
    requestedActiveId,
  );
  const safeProfiles = profiles.length ? profiles : [defaultProfileForCountry(countryHint)];
  const activeProfileId = safeProfiles.some((profile) => profile.id === requestedActiveId)
    ? requestedActiveId
    : safeProfiles[0].id;
  return { profiles: safeProfiles, activeProfileId };
}

export function customBriefProfiles(collection: BriefProfileCollection): BriefProfile[] {
  return normalizeBriefProfileCollection(collection).profiles.filter(
    (profile) => !profile.templateId,
  );
}

export function canAddCustomBriefProfile(collection: BriefProfileCollection): boolean {
  return customBriefProfiles(collection).length < MAX_CUSTOM_BRIEF_PROFILES;
}

export function canPersistReadyMadeBriefProfile(collection: BriefProfileCollection): boolean {
  const current = normalizeBriefProfileCollection(collection);
  return (
    current.profiles.some((profile) => Boolean(profile.templateId)) ||
    current.profiles.length < MAX_BRIEF_PROFILES
  );
}

export function canCustomizeActiveBriefProfile(collection: BriefProfileCollection): boolean {
  const current = normalizeBriefProfileCollection(collection);
  const active =
    current.profiles.find((profile) => profile.id === current.activeProfileId) ||
    current.profiles[0];
  return !active.templateId || canAddCustomBriefProfile(current);
}

export function briefProfileCollectionFromAndroidSyncPreferences(
  preferences: AndroidBriefProfileSyncPreferences | null | undefined,
  countryHint = "US",
): BriefProfileCollection | null {
  const wires = parseAndroidBriefProfiles(preferences?.briefProfilesJson);
  if (!wires.length) return null;
  return normalizeBriefProfileCollection(
    {
      profiles: wires.map((wire) => briefProfileFromAndroidWire(wire, countryHint)),
      activeProfileId: String(preferences?.activeBriefProfileId || "").trim(),
    },
    countryHint,
  );
}

/**
 * Converts the active profile from Android's canonical preference fields into
 * the legacy single-profile web model. Inactive profiles remain available via
 * [briefProfileCollectionFromAndroidSyncPreferences].
 */
export function briefProfileFromAndroidSyncPreferences(
  preferences: AndroidBriefProfileSyncPreferences | null | undefined,
  countryHint = "US",
): BriefProfile | null {
  const collection = briefProfileCollectionFromAndroidSyncPreferences(preferences, countryHint);
  return (
    collection?.profiles.find((profile) => profile.id === collection.activeProfileId) ||
    collection?.profiles[0] ||
    null
  );
}

export function briefProfilesJsonForAndroid(collection: BriefProfileCollection): string {
  const normalized = normalizeBriefProfileCollection(collection);
  return JSON.stringify(
    normalized.profiles.map((profile) => ({
      id: profile.id,
      name: profile.name,
      sectionIds: profile.sectionIds,
      disabledSectionIds: profile.disabledSectionIds,
      purpose: profile.purpose,
      preferredLength: profile.preferredLength,
      tone: profile.tone,
      timeOfDay: profile.timeOfDay,
      templateId: profile.templateId,
      narrationEnabled: profile.narrationEnabled,
      languageCode: profile.languageCode,
      createdAtEpochMs: profile.createdAtEpochMs || 0,
      updatedAtEpochMs: profile.updatedAtEpochMs || 0,
    })),
  );
}

export function loadBriefProfileCollection(countryHint = "US"): BriefProfileCollection {
  const fallback = defaultProfileForCountry(countryHint);
  try {
    const rawCollection = readAccountScopedStorage(BRIEF_PROFILES_KEY);
    if (rawCollection) {
      return normalizeBriefProfileCollection(JSON.parse(rawCollection), countryHint);
    }
    const rawProfile = readAccountScopedStorage(BRIEF_PROFILE_KEY);
    return normalizeBriefProfileCollection(
      rawProfile
        ? { profiles: [JSON.parse(rawProfile) as Partial<BriefProfile>] }
        : { profiles: [fallback] },
      countryHint,
    );
  } catch {
    return normalizeBriefProfileCollection({ profiles: [fallback] }, countryHint);
  }
}

function emitProfileChanged(profile: BriefProfile, collection: BriefProfileCollection): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(BRIEF_PROFILE_CHANGED_EVENT, {
      detail: {
        profile,
        profiles: collection.profiles,
        activeProfileId: collection.activeProfileId,
      },
    }),
  );
}

export function saveBriefProfileCollection(
  collection: BriefProfileCollection,
): BriefProfileCollection {
  const countryHint =
    collection.profiles.find((profile) => profile.id === collection.activeProfileId)
      ?.marketCountry || collection.profiles[0]?.marketCountry;
  const next = normalizeBriefProfileCollection(collection, countryHint);
  const active = next.profiles.find((profile) => profile.id === next.activeProfileId)!;
  if (storage()) {
    try {
      writeAccountScopedStorage(BRIEF_PROFILES_KEY, JSON.stringify(next));
      writeAccountScopedStorage(BRIEF_PROFILE_KEY, JSON.stringify(active));
    } catch {
      /* profile writes are best effort */
    }
  }
  emitProfileChanged(active, next);
  return next;
}

export function loadBriefProfile(countryHint = "US"): BriefProfile {
  const collection = loadBriefProfileCollection(countryHint);
  return (
    collection.profiles.find((profile) => profile.id === collection.activeProfileId) ||
    collection.profiles[0]
  );
}

export function saveBriefProfile(profile: BriefProfile): BriefProfile {
  const next = normalizeBriefProfile(profile);
  const collection = loadBriefProfileCollection(next.marketCountry);
  const profileIndex = collection.profiles.findIndex((entry) => entry.id === next.id);
  const profiles = [...collection.profiles];
  if (profileIndex >= 0) profiles[profileIndex] = next;
  else if (profiles.length < MAX_BRIEF_PROFILES) profiles.push(next);
  else profiles[profiles.length - 1] = next;
  const saved = saveBriefProfileCollection({ profiles, activeProfileId: next.id });
  return saved.profiles.find((entry) => entry.id === saved.activeProfileId)!;
}

export function newBriefProfileId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `web-brief-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export function createBriefProfileFromActive(
  collection: BriefProfileCollection,
  name: string,
  nowEpochMs = Date.now(),
): BriefProfileCollection {
  const current = normalizeBriefProfileCollection(collection);
  if (!canAddCustomBriefProfile(current)) return current;
  const active =
    current.profiles.find((profile) => profile.id === current.activeProfileId) ||
    current.profiles[0];
  const created = normalizeBriefProfile({
    ...active,
    id: newBriefProfileId(),
    name: name.trim() || `Custom style ${customBriefProfiles(current).length + 1}`,
    purpose: "custom",
    preferredLength: "balanced",
    depth: "balanced",
    targetMinutes: 5,
    tone: "calm",
    timeOfDay: "anytime",
    templateId: "",
    styleId: "daily",
    narrationEnabled: false,
    createdAtEpochMs: nowEpochMs,
    updatedAtEpochMs: nowEpochMs,
  });
  return saveBriefProfileCollection({
    profiles: [...current.profiles, created],
    activeProfileId: created.id,
  });
}

export function selectBriefProfile(
  collection: BriefProfileCollection,
  profileId: string,
  nowEpochMs = Date.now(),
): BriefProfileCollection {
  const current = normalizeBriefProfileCollection(collection);
  if (!current.profiles.some((profile) => profile.id === profileId)) return current;
  return saveBriefProfileCollection({
    profiles: current.profiles.map((profile) =>
      profile.id === profileId ? { ...profile, updatedAtEpochMs: nowEpochMs } : profile,
    ),
    activeProfileId: profileId,
  });
}

export function deleteBriefProfile(
  collection: BriefProfileCollection,
  profileId: string,
): BriefProfileCollection {
  const current = normalizeBriefProfileCollection(collection);
  if (current.profiles.length <= 1) return current;
  const profiles = current.profiles.filter((profile) => profile.id !== profileId);
  if (profiles.length === current.profiles.length) return current;
  return saveBriefProfileCollection({
    profiles,
    activeProfileId:
      current.activeProfileId === profileId ? profiles[0].id : current.activeProfileId,
  });
}

export function reorderBriefProfiles(
  collection: BriefProfileCollection,
  orderedIds: string[],
): BriefProfileCollection {
  const current = normalizeBriefProfileCollection(collection);
  const byId = new Map(current.profiles.map((profile) => [profile.id, profile]));
  const ordered = Array.from(new Set(orderedIds)).flatMap((id) => {
    const profile = byId.get(id);
    if (!profile) return [];
    byId.delete(id);
    return [profile];
  });
  const profiles = [...ordered, ...current.profiles.filter((profile) => byId.has(profile.id))];
  return saveBriefProfileCollection({ profiles, activeProfileId: current.activeProfileId });
}

export function updateActiveBriefProfile(
  collection: BriefProfileCollection,
  update: (profile: BriefProfile) => BriefProfile,
  options: { customizeLineup?: boolean; nowEpochMs?: number } = {},
): BriefProfileCollection {
  const current = normalizeBriefProfileCollection(collection);
  const active =
    current.profiles.find((profile) => profile.id === current.activeProfileId) ||
    current.profiles[0];
  const nowEpochMs = options.nowEpochMs ?? Date.now();
  if (options.customizeLineup && active.templateId) {
    if (!canAddCustomBriefProfile(current)) return current;
    const custom = normalizeBriefProfile({
      ...update(active),
      id: newBriefProfileId(),
      name: "My Brief",
      purpose: "custom",
      templateId: "",
      styleId: "daily",
      createdAtEpochMs: nowEpochMs,
      updatedAtEpochMs: nowEpochMs,
    });
    return saveBriefProfileCollection({
      profiles: [...current.profiles, custom],
      activeProfileId: custom.id,
    });
  }
  const nextActive = normalizeBriefProfile({
    ...update(active),
    ...(options.customizeLineup
      ? {
          purpose: "custom" as const,
          templateId: "",
          styleId: "daily" as const,
        }
      : {}),
    updatedAtEpochMs: nowEpochMs,
  });
  return saveBriefProfileCollection({
    profiles: current.profiles.map((profile) => (profile.id === active.id ? nextActive : profile)),
    activeProfileId: active.id,
  });
}

export function onBriefProfileChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(BRIEF_PROFILE_CHANGED_EVENT, listener);
  return () => window.removeEventListener(BRIEF_PROFILE_CHANGED_EVENT, listener);
}

function newRunId(): string {
  try {
    return `brief-run-${crypto.randomUUID()}`;
  } catch {
    return `brief-run-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function normalizeRun(value: Partial<BriefRunRecord>): BriefRunRecord | null {
  const completedAtEpochMs = Number(value.completedAtEpochMs) || 0;
  if (!completedAtEpochMs) return null;
  const depth = normalizeDepth(value.depth);
  const storedMinutes = Number(value.targetMinutes);
  const targetMinutes: BriefReadMinutes =
    storedMinutes === 3 || storedMinutes === 8
      ? storedMinutes
      : storedMinutes === 5 && depth !== "deep"
        ? 5
        : briefReadMinutesForLength(briefProfileLengthForDepth(depth));
  return {
    id: String(value.id || newRunId()),
    completedAtEpochMs,
    sectionCount: Math.max(0, Number(value.sectionCount) || 0),
    targetMinutes,
    depth,
  };
}

export function loadBriefRunHistory(): BriefRunRecord[] {
  const store = storage();
  if (!store) return [];
  try {
    const raw = readAccountScopedStorage(BRIEF_HISTORY_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<BriefRunRecord>[]) : [];
    return (Array.isArray(parsed) ? parsed : [])
      .map(normalizeRun)
      .filter((run): run is BriefRunRecord => Boolean(run))
      .sort((a, b) => b.completedAtEpochMs - a.completedAtEpochMs)
      .slice(0, 10);
  } catch {
    return [];
  }
}

function localDayKey(epochMs: number): string {
  const date = new Date(epochMs);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function localDateIso(epochMs: number): string {
  const date = new Date(epochMs);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function briefRunStreak(history: BriefRunRecord[], nowEpochMs = Date.now()): number {
  const completedDays = new Set(
    history
      .map((run) => Number(run.completedAtEpochMs))
      .filter((epochMs) => Number.isFinite(epochMs) && epochMs > 0 && epochMs <= nowEpochMs)
      .map(localDayKey),
  );
  let cursor = new Date(nowEpochMs);
  cursor.setHours(0, 0, 0, 0);
  if (!completedDays.has(localDayKey(cursor.getTime()))) {
    cursor.setDate(cursor.getDate() - 1);
  }
  let streak = 0;
  while (completedDays.has(localDayKey(cursor.getTime()))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

function seedBriefStreakState(
  history: BriefRunRecord[],
  nowEpochMs = Date.now(),
): BriefStreakSyncState {
  const latest = history.find(
    (run) =>
      Number.isFinite(run.completedAtEpochMs) &&
      run.completedAtEpochMs > 0 &&
      run.completedAtEpochMs <= nowEpochMs,
  );
  const count = briefRunStreak(history, nowEpochMs);
  return {
    count,
    best: count,
    lastDate: latest ? localDateIso(latest.completedAtEpochMs) : "",
    updatedAtEpochMs: latest?.completedAtEpochMs || 0,
  };
}

export function loadBriefStreakSyncState(history = loadBriefRunHistory()): BriefStreakSyncState {
  if (!storage()) return seedBriefStreakState(history);
  try {
    const raw = readAccountScopedStorage(BRIEF_STREAK_KEY);
    if (!raw) return seedBriefStreakState(history);
    return normalizeBriefStreakState(JSON.parse(raw));
  } catch {
    return seedBriefStreakState(history);
  }
}

function saveBriefStreakSyncState(state: BriefStreakSyncState): BriefStreakSyncState {
  const normalized = normalizeBriefStreakState(state);
  try {
    writeAccountScopedStorage(BRIEF_STREAK_KEY, JSON.stringify(normalized));
  } catch {
    return normalized;
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(BRIEF_STREAK_CHANGED_EVENT, { detail: normalized }));
  }
  return normalized;
}

export function applyBriefStreakSyncPayload(
  payload: unknown,
  streakUpdatedAt: unknown,
  serverTime: unknown,
  preferRemote = false,
): BriefStreakSyncState {
  const remoteUpdatedAtEpochMs = Date.parse(String(streakUpdatedAt || ""));
  const current = loadBriefStreakSyncState();
  const merged = mergeBriefStreakStates(
    current,
    {
      ...(payload && typeof payload === "object" && !Array.isArray(payload) ? payload : {}),
      updatedAtEpochMs: Number.isFinite(remoteUpdatedAtEpochMs) ? remoteUpdatedAtEpochMs : 0,
    },
    {
      remoteTimestampProvided: String(streakUpdatedAt || "").trim().length > 0,
      preferRemote,
      maximumActivityDate: maximumAcceptedBriefStreakDate(serverTime || Date.now()),
    },
  );
  if (
    merged.count !== current.count ||
    merged.best !== current.best ||
    merged.lastDate !== current.lastDate ||
    merged.updatedAtEpochMs !== current.updatedAtEpochMs
  ) {
    return saveBriefStreakSyncState(merged);
  }
  return current;
}

export function onBriefStreakChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(BRIEF_STREAK_CHANGED_EVENT, listener);
  return () => window.removeEventListener(BRIEF_STREAK_CHANGED_EVENT, listener);
}

export function recordBriefRun(input: {
  sectionCount: number;
  profile: BriefProfile;
}): BriefRunRecord[] {
  const run: BriefRunRecord = {
    id: newRunId(),
    completedAtEpochMs: Date.now(),
    sectionCount: input.sectionCount,
    targetMinutes: briefReadMinutesForLength(input.profile.preferredLength),
    depth: input.profile.depth,
  };
  const next = [run, ...loadBriefRunHistory()].slice(0, 10);
  const store = storage();
  if (store) {
    try {
      writeAccountScopedStorage(BRIEF_HISTORY_KEY, JSON.stringify(next));
    } catch {
      /* history writes are best effort */
    }
  }
  const currentStreak = loadBriefStreakSyncState(next.slice(1));
  const creditedStreak = creditBriefStreakCompletion(
    currentStreak,
    localDateIso(run.completedAtEpochMs),
    run.completedAtEpochMs,
  );
  if (
    creditedStreak.count !== currentStreak.count ||
    creditedStreak.best !== currentStreak.best ||
    creditedStreak.lastDate !== currentStreak.lastDate ||
    creditedStreak.updatedAtEpochMs !== currentStreak.updatedAtEpochMs
  ) {
    saveBriefStreakSyncState(creditedStreak);
  }
  return next;
}
